import React from "react";
import * as Yup from "yup";
import { useParams, Link } from "react-router-dom";
import { Row, Col, Card, Label } from "reactstrap";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from "primereact/dropdown";
import EditorContainer from "../../components/EditorContainer";
import Address from "../../components/Address";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { createCustomer, updateCustomer, getCustomer, getCustomerVehicles } from "../../api/customers";
import { useAuth0 } from "../../auth/react-auth0-wrapper";
import { addressSchema } from "../../utils/customValidators";
import { canUserSave } from "../../auth/Can";
import roles from "../../auth/roles";

const schema = () =>
    Yup.object({
        name: Yup.string()
            .required("Ragione Sociale/Cognome Nome è un campo obbligatorio")
            .nullable(),
        taxcode: Yup.string().required("Il Codice Fiscale o la Partita Iva è un campo obbligatorio"),
        email: Yup.string()
            .required("Email è un campo obbligatorio")
            .email("Inserire una email valida"),
        category: Yup.string()
            .required("Il tipo di Cliente è obbligatorio")
            .nullable(),
        attributes: Yup.object({
            phone: Yup.string().required("Telefono è un campo obbligatorio")
        })
        /* , address: addressSchema */
    });

const CustomerEditor = ({ activeTenant, onCreated }) => {

    const { id } = useParams();
    const { domains } = useAuth0();
    const [vehicles, setVehicles] = React.useState([]);

    const onCreate = async entity => {
        const result = await createCustomer(
            {
                ...entity,
                tenant_id: activeTenant._id
            }
        );
        if (onCreated) {
            onCreated(result);
            return false;
        }
        return result;
    };

    const onLoad = async id => {
        if (onCreated) return
        const customer = await getCustomer(id);
        const vehiclesRes = await getCustomerVehicles(id);
        setVehicles(vehiclesRes);
        return customer;
    };

    const onUpdate = async entity => {
        const result = await updateCustomer(entity);
        return result;
    };

    const onAttributesChange = (
        changedProps,
        attributes,
        containerHandleChange
    ) => {
        const newAttributes = { ...attributes, ...changedProps };
        containerHandleChange("attributes", { target: { value: newAttributes } });
    };

    return (
        <EditorContainer
            key={id ? `${id}-customer` : "new-customer"}
            canSave={canUserSave([roles.VENDITORE, roles.BACKOFFICE, roles.DEALER, roles.ADMIN])}
            schema={schema}
            onCreate={onCreate}
            onUpdate={onUpdate}
            onLoad={onLoad}
            asSelector={onCreated ? true : false}
            optionalNav={{ link: `/tenant/customers`, text: "Clienti" }}
            redirRoot={`/tenant/customers`}
            title={el =>
                `${el.name ? el.name : "NUOVO CLIENTE"}`
            }
            children={(frmProps, containerHandleChange) => {
                const attributes = frmProps.values["attributes"] || {
                    codelinv: "",
                    phone: "",
                    notes: "",
                    reference1: "",
                    reftel1: "",
                    refemail1: "",
                    mansione1: "",
                    reference2: "",
                    reftel2: "",
                    refemail2: "",
                    mansione2: "",
                    ticket: "",
                    note: ""
                };
                const attributesErrors = frmProps.errors.attributes || {};
                return (
                    <Row>
                        <Col sm="6" xs="12">
                            <strong className="mt-2">Ragione Sociale / Cognome - Nome</strong>
                            <InputText
                                className={frmProps.errors.name ? "p-error" : ""}
                                value={frmProps.values["name"] || ""}
                                style={{ width: "100%" }}
                                onChange={e => containerHandleChange("name", e)}
                            />
                            {frmProps.errors.name ? (<div className="invalid-feedback">{frmProps.errors.name}</div>) : null}
                        </Col>
                        <Col sm="6" xs="12">
                            <strong className="mt-2">Tipo di Cliente</strong>
                            <Dropdown
                                filter
                                filterBy="label"
                                id="type"
                                className={frmProps.errors.category ? "p-error" : ""}
                                value={frmProps.values["category"]}
                                showClear={true}
                                style={{ width: "100%" }}
                                options={domains.customersType}
                                onChange={e => containerHandleChange("category", e)}
                            />
                            {frmProps.errors.category ? (<div className="invalid-feedback">{frmProps.errors.category}</div>) : null}
                        </Col>
                        <Address
                            errors={frmProps.errors.address || {}}
                            address={frmProps.values["address"] || {}}
                            onChange={e => containerHandleChange("address", e)}
                        />
                        <Col sm="6" xs="12">
                            <strong className="mt-2">Codice Fiscale</strong>
                            <InputText
                                className={frmProps.errors.taxcode ? "p-error" : ""}
                                value={frmProps.values["taxcode"] || ""}
                                style={{ width: "100%" }}
                                onChange={e => containerHandleChange("taxcode", e)}
                            />
                            {frmProps.errors.taxcode ? (<div className="invalid-feedback">{frmProps.errors.taxcode}</div>) : null}
                        </Col>
                        <Col sm="6" xs="12">
                            <strong className="mt-2">Partita Iva</strong>
                            <InputText
                                className={attributesErrors.piva ? "p-error" : ""}
                                value={attributes.piva || ""}
                                style={{ width: "100%" }}
                                onChange={e =>
                                    onAttributesChange(
                                        { piva: e.target.value },
                                        attributes,
                                        containerHandleChange
                                    )
                                }
                            />
                            {frmProps.errors.taxcode ? (<div className="invalid-feedback">{frmProps.errors.taxcode}</div>) : null}
                        </Col>
                        <Col sm="6" xs="12">
                            <strong className="mt-2">Telefono</strong>
                            <InputText
                                className={attributesErrors.phone ? "p-error" : ""}
                                value={attributes.phone || ""}
                                style={{ width: "100%" }}
                                onChange={e =>
                                    onAttributesChange(
                                        { phone: e.target.value },
                                        attributes,
                                        containerHandleChange
                                    )
                                }
                            />
                            {attributesErrors.phone ? (<div className="invalid-feedback">{attributesErrors.phone}</div>) : null}
                        </Col>
                        <Col sm="6" xs="12">
                        </Col>
                        <Col sm="6" xs="12">
                            <strong className="mt-2">Email</strong>
                            <InputText
                                keyfilter="email"
                                className={frmProps.errors.email ? "p-error" : ""}
                                value={frmProps.values["email"] || ""}
                                style={{ width: "100%" }}
                                onChange={e => containerHandleChange("email", e)}
                            />
                            {frmProps.errors.email ? (<div className="invalid-feedback">{frmProps.errors.email}</div>) : null}
                        </Col>
                        <Col sm="6" xs="12">
                            <strong className="mt-2">PEC</strong>
                            <InputText
                                className={attributesErrors.pec ? "p-error" : ""}
                                value={attributes.pec || ""}
                                style={{ width: "100%" }}
                                onChange={e =>
                                    onAttributesChange(
                                        { pec: e.target.value },
                                        attributes,
                                        containerHandleChange
                                    )
                                }
                            />
                        </Col>
                        <Col sm="6" xs="12">
                            <strong className="mt-2">Cod. Fatturazione Elettronica</strong>
                            <InputText
                                className={attributesErrors.codelinv ? "p-error" : ""}
                                value={attributes.codelinv || ""}
                                style={{ width: "100%" }}
                                onChange={e =>
                                    onAttributesChange(
                                        { codelinv: e.target.value.toUpperCase() },
                                        attributes,
                                        containerHandleChange
                                    )
                                }
                            />
                        </Col>
                        <Col sm="6" xs="12">
                            <strong className="mt-2 text-danger">Cod. Centrale Operativa</strong>
                            <InputText
                                className={attributesErrors.ticket ? "p-error" : ""}
                                value={attributes.ticket || ""}
                                style={{ width: "100%" }}
                                onChange={e =>
                                    onAttributesChange(
                                        { ticket: e.target.value },
                                        attributes,
                                        containerHandleChange
                                    )
                                }
                            />
                        </Col>
                        <Col sm="12" xs="12">
                            <strong className="mt-2">Note</strong>
                            <InputTextarea
                                className={attributesErrors.note ? "p-error" : ""}
                                value={attributes.note || ""}
                                style={{ width: "100%" }}
                                rows={2}
                                onChange={e =>
                                    onAttributesChange(
                                        { note: e.target.value },
                                        attributes,
                                        containerHandleChange
                                    )
                                }
                            />
                        </Col>
                        <Col sm="6" xs="12">
                            <Card body className="card-box border-1 card-shadow-first mt-2">
                                <strong className="text-primary" >Persona di riferimento 1</strong>
                                <Row>
                                    <Col md="6" sm="6" xs="12">
                                        <strong className="mt-2">Nominativo</strong>
                                        <InputText
                                            className={attributesErrors.reference1 ? "p-error" : ""}
                                            value={attributes.reference1}
                                            style={{ width: "100%" }}
                                            onChange={e =>
                                                onAttributesChange(
                                                    { reference1: e.target.value },
                                                    attributes,
                                                    containerHandleChange
                                                )
                                            }
                                        />
                                        <strong className="mt-2">Telefono</strong>
                                        <InputText
                                            className={attributesErrors.reftel1 ? "p-error" : ""}
                                            value={attributes.reftel1}
                                            style={{ width: "100%" }}
                                            onChange={e =>
                                                onAttributesChange(
                                                    { reftel1: e.target.value },
                                                    attributes,
                                                    containerHandleChange
                                                )
                                            }
                                        />
                                    </Col>
                                    <Col md="6" sm="6" xs="12">
                                        <strong className="mt-2">Email</strong>
                                        <InputText
                                            className={attributesErrors.refemail1 ? "p-error" : ""}
                                            value={attributes.refemail1}
                                            style={{ width: "100%" }}
                                            onChange={e =>
                                                onAttributesChange(
                                                    { refemail1: e.target.value },
                                                    attributes,
                                                    containerHandleChange
                                                )
                                            }
                                        />
                                        <strong className="mt-2">Mansione</strong>
                                        <InputText
                                            className={attributesErrors.mansione1 ? "p-error" : ""}
                                            value={attributes.mansione1}
                                            style={{ width: "100%" }}
                                            onChange={e =>
                                                onAttributesChange(
                                                    { mansione1: e.target.value },
                                                    attributes,
                                                    containerHandleChange
                                                )
                                            }
                                        />
                                    </Col>
                                </Row>
                            </Card>
                        </Col>
                        <Col sm="6" xs="12">
                            <Card body className="card-box border-1 card-shadow-first mt-2">
                                <strong className="text-primary">Persona di riferimento 2</strong>
                                <Row>
                                    <Col md="6" sm="6" xs="12">
                                        <strong className="mt-2">Nominativo</strong>
                                        <InputText
                                            className={attributesErrors.reference2 ? "p-error" : ""}
                                            value={attributes.reference2}
                                            style={{ width: "100%" }}
                                            onChange={e =>
                                                onAttributesChange(
                                                    { reference2: e.target.value },
                                                    attributes,
                                                    containerHandleChange
                                                )
                                            }
                                        />
                                        <strong className="mt-2">Telefono</strong>
                                        <InputText
                                            className={attributesErrors.reftel2 ? "p-error" : ""}
                                            value={attributes.reftel2}
                                            style={{ width: "100%" }}
                                            onChange={e =>
                                                onAttributesChange(
                                                    { reftel2: e.target.value },
                                                    attributes,
                                                    containerHandleChange
                                                )
                                            }
                                        />
                                    </Col>
                                    <Col md="6" sm="6" xs="12">
                                        <strong className="mt-2">Email</strong>
                                        <InputText
                                            className={attributesErrors.refemail2 ? "p-error" : ""}
                                            value={attributes.refemail2}
                                            style={{ width: "100%" }}
                                            onChange={e =>
                                                onAttributesChange(
                                                    { refemail2: e.target.value },
                                                    attributes,
                                                    containerHandleChange
                                                )
                                            }
                                        />
                                        <strong className="mt-2">Mansione</strong>
                                        <InputText
                                            className={attributesErrors.mansione2 ? "p-error" : ""}
                                            value={attributes.mansione2}
                                            style={{ width: "100%" }}
                                            onChange={e =>
                                                onAttributesChange(
                                                    { mansione2: e.target.value },
                                                    attributes,
                                                    containerHandleChange
                                                )
                                            }
                                        />
                                    </Col>
                                </Row>
                            </Card>
                        </Col>
                    </Row>
                );
            }}
            renderDealer={() =>
                activeTenant ? (
                    <React.Fragment>
                        <Card body className="rounded bg-secondary mb-2">
                            <Row>
                                <Col sm="4" xs="12">
                                    <strong>Dealer: </strong>
                                    <Label className="h5">{activeTenant.name}</Label>
                                </Col>
                                <Col sm="4" xs="12" md="3">
                                    <strong>Telefono: </strong>
                                    <Label className="h5">{activeTenant.phone}</Label>
                                </Col>
                                <Col sm="4" xs="12" md="3">
                                    <strong>Email: </strong>
                                    <Label className="h5">{activeTenant.email}</Label>
                                </Col>
                            </Row>
                        </Card>
                    </React.Fragment>
                ) : (
                        <Spinner style={{ width: "3rem", height: "3rem" }} type="grow" />
                    )
            }

            renderOther={() =>
                activeTenant ? (
                    <Row>
                        {id && (
                            <Col>
                                <div><strong className="mt-2">Aggiungi veicolo al Cliente</strong></div>
                                <Link
                                    title="aggiungi veicolo"
                                    className="btn btn-primary p-button-icon-only mr-2"
                                    to={`/tenant/customers/${id}/add-vehicle/manually`}
                                >
                                    <FontAwesomeIcon icon="car" className="mr-1" />Aggiungi veicolo
                                </Link>
                                <Link
                                    title="aggiungi veicolo da targa"
                                    className="btn btn-primary p-button-icon-only"
                                    to={`/tenant/customers/${id}/add-vehicle/from-plate`}
                                >
                                    <FontAwesomeIcon icon="taxi" className="mr-1" />Aggiungi veicolo da targa
                                </Link>
                            </Col>
                        )}
                        {id && (
                            <Col xs="12">
                                <div className="divider my-3" />
                                <strong className="mt-2 text-primary">Veicoli:</strong>
                                <DataTable
                                    value={vehicles || []}
                                    paginator={false}
                                    autoLayout={false}
                                    className="p-datatable-hoverable-rows p-datatable-customers"
                                    emptyMessage={() => (<div className="text-center">Nessun Veicolo in tabella</div>)}
                                    paginatorTemplate="RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                                    currentPageReportTemplate="Visualizzati {first} di {last} Veicoli su un totale di {totalRecords}"
                                >
                                    <Column
                                        field="plate"
                                        style={{ wordWrap: "break-word" }}
                                        header="Targa"
                                        sortable
                                        filter
                                        filterMatchMode="contains"
                                        body={(rowData, column) => {
                                            return (
                                                <Link
                                                    to={`/tenant/vehicles/${rowData._id}`}
                                                >
                                                    {rowData.plate.toUpperCase()}
                                                </Link>
                                            );
                                        }}
                                    />
                                    <Column
                                        field="attributes.frame"
                                        style={{ wordWrap: "break-word" }}
                                        header="Telaio"
                                        sortable
                                        filter
                                        filterMatchMode="contains"
                                    />
                                    <Column
                                        field="attributes.preparation"
                                        style={{ wordWrap: "break-word" }}
                                        header="Allestimento"
                                        sortable
                                        filter
                                        filterMatchMode="contains"
                                    />
                                    <Column
                                        field="km"
                                        style={{ wordWrap: "break-word" }}
                                        header="Km"
                                        sortable
                                        filter
                                        filterMatchMode="contains"
                                    />
                                    <Column
                                        field="km"
                                        style={{ wordWrap: "break-word" }}
                                        header=""
                                        body={(v) => (<Link
                                            title="modifica"
                                            className="btn btn-sm btn-primary p-button-icon-only"
                                            to={`/tenant/vehicles/${v._id}`}
                                        >
                                            <FontAwesomeIcon icon="pencil-alt" className="mr-1" />
                                        </Link>)}
                                    />
                                </DataTable>
                            </Col>
                        )}

                    </Row>
                ) : null
            }
        />
    );
};
export default CustomerEditor;
